import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../api";
import { useI18n } from "../i18n";
import { useSEO } from "../seo";

const CATEGORIES = [
  { key: "", labelEn: "All", labelFr: "Tout" },
  { key: "tops", labelEn: "Tops", labelFr: "Hauts" },
  { key: "bottoms", labelEn: "Bottoms", labelFr: "Bas" },
  { key: "jewelry", labelEn: "Jewelry", labelFr: "Joaillerie" },
  { key: "accessories", labelEn: "Accessories", labelFr: "Accessoires" },
];

export default function Shop() {
  const { t, lang } = useI18n();
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("");

  useSEO({
    title: lang === "fr" ? "Boutique — Beaudoin" : "Shop — Beaudoin",
    description: lang === "fr"
      ? "Découvrez la Collection Souveraine de Beaudoin. Streetwear de luxe gothique, éditions limitées, fait à Saint-Barth."
      : "Discover the Beaudoin Sovereign Collection. Gothic luxury streetwear, limited editions, made in Saint Barth.",
  });

  useEffect(() => {
    api.get("/products", { params: category ? { category } : {} })
      .then((r) => setProducts(r.data)).catch(() => {});
  }, [category]);

  return (
    <div className="bg-black text-white pt-32 pb-32">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16">
          <div className="md:col-span-8">
            <div className="text-xs font-bold tracking-[0.3em] uppercase text-blood mb-6">{t.featured.kicker}</div>
            <h1 className="font-black text-5xl md:text-7xl lg:text-8xl leading-[0.85] tracking-tighter uppercase">
              The <span className="text-blood">Sovereign</span><br/>Collection
            </h1>
          </div>
          <div className="md:col-span-4 text-xs font-bold tracking-[0.2em] uppercase text-white/40">
            {products.length} {lang === "fr" ? "pièces" : "pieces"}
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-16 pb-6 border-b border-white/10">
          {CATEGORIES.map((c) => (
            <button key={c.key || "all"} data-testid={`category-${c.key || "all"}`}
              onClick={() => setCategory(c.key)}
              className={`px-5 py-2 text-xs font-bold tracking-[0.2em] uppercase border transition-colors ${
                category === c.key ? "bg-blood border-blood text-white" : "border-white/20 text-white/60 hover:border-white hover:text-white"
              }`}>
              {lang === "fr" ? c.labelFr : c.labelEn}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 md:gap-x-8 gap-y-12 md:gap-y-24">
          {products.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: (i % 8) * 0.05 }}
              className={i % 5 === 0 ? "md:translate-y-12" : ""}>
              <Link to={`/product/${p.id}`} data-testid={`product-card-${p.id}`} className="group block">
                <div className="aspect-[3/4] overflow-hidden bg-[#0a0707] relative">
                  <img src={p.image} alt={p.name}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ease-out" />
                  {p.featured && (
                    <div className="absolute top-4 left-4 bg-blood px-3 py-1 text-[10px] font-bold tracking-[0.2em] uppercase">
                      {lang === "fr" ? "Phare" : "Featured"}
                    </div>
                  )}
                </div>
                <div className="pt-4">
                  <div className="text-sm font-bold tracking-wide">{lang === "fr" ? p.name_fr : p.name}</div>
                  <div className="text-xs text-bone mt-1">€{p.price.toFixed(0)}</div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
